import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Divider,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';

import { EDigestPreference } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';

import {
  CompanyNotifications_UpdateOrganizationLinks,
  CompanyNotifications_UpdateOrganizationLinksVariables,
} from './graphql-types/CompanyNotifications_UpdateOrganizationLinks';
import {
  CompanyNotifications_UserOrganizationLinks,
  CompanyNotifications_UserOrganizationLinksVariables,
} from './graphql-types/CompanyNotifications_UserOrganizationLinks';

import { LinkDigestPrefListItem } from '../LinkDigestPrefListItem/LinkDigestPrefListItem';

const GET_ORGANIZATION_LINKS = gql`
  query CompanyNotifications_UserOrganizationLinks(
    $input: MultiUserOrganizationLinkInput!
  ) {
    userOrganizationLinks(input: $input) {
      results {
        _id
        digestPreference
        organization {
          _id
          name
          defaultDigestPref
        }
      }
    }
  }
`;

const UPDATE_ORAGANIZATION_LINKS = gql`
  mutation CompanyNotifications_UpdateOrganizationLinks(
    $input: UpdateUserOrganizationLinkInput!
  ) {
    updateUserOrganizationLink(input: $input) {
      data {
        _id
        digestPreference
      }
    }
  }
`;

export const CompanyNotifications: React.FC<{
  currentUserId: string;
  isDisabled: boolean;
}> = ({ currentUserId, isDisabled }) => {
  const { loading, data, error } = useQuery<
    CompanyNotifications_UserOrganizationLinks,
    CompanyNotifications_UserOrganizationLinksVariables
  >(GET_ORGANIZATION_LINKS, {
    variables: {
      input: {
        filter: {
          userId: {
            _eq: currentUserId,
          },
        },
        limit: 100,
        offset: 0,
      },
    },
  });

  const [updateUserOrganizationLink] = useMutation<
    CompanyNotifications_UpdateOrganizationLinks,
    CompanyNotifications_UpdateOrganizationLinksVariables
  >(UPDATE_ORAGANIZATION_LINKS, {
    refetchQueries: ['CompanyNotifications_UserOrganizationLinks'],
    awaitRefetchQueries: true,
  });

  const toast = useToast();

  const onChange = async (options: {
    pref: EDigestPreference;
    userOrganizationLinkId: string;
  }): Promise<void> => {
    try {
      await updateUserOrganizationLink({
        variables: {
          input: {
            id: options.userOrganizationLinkId,
            data: {
              digestPreference: options.pref,
            },
          },
        },
      });

      toast({ status: 'success', description: 'Updated preference' });
    } catch (err) {
      toast({ status: 'error', description: 'Failed to update preference.' });
      logError(err);
    }
  };

  if (error) {
    throw error;
  }

  const organizationLinks = data?.userOrganizationLinks?.results;

  if (loading || !organizationLinks) {
    return null;
  }

  const sorted = [...organizationLinks].sort((a, b) =>
    a.organization.name < b.organization.name ? -1 : 1
  );

  return (
    <Card overflow="hidden">
      <Stack spacing="0">
        <Text fontSize="sm" p="4" bg="secondary.500" color="textPrimary.500" fontWeight="600">
          Sponsors
        </Text>
        <Divider />
        <Stack py="4" spacing="2">
          {sorted.map((link, index) => (
            <LinkDigestPrefListItem
              key={link._id}
              link={{
                ...link,
                digestPreference: !!link.digestPreference
                  ? (link.digestPreference as EDigestPreference)
                  : undefined,
                organization: {
                  ...link.organization,
                  defaultDigestPref: link.organization
                    .defaultDigestPref as EDigestPreference,
                },
              }}
              onChange={onChange}
              isDisabled={isDisabled}
              isLast={index === sorted.length - 1}
            />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
};
